/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 윈도우 클래스를 정의한다.
 *
 * @file /src/components/window/Window.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 18.
 */
import * as parent from '@components/panel/Panel';
import { Viewport } from '@base/Viewport';
import { Component } from '@base/Component';
import { Button } from '@components/button/Button';
import { Task } from '@components/taskbar/elements/Task';
import { Toolbar, Properties as ToolbarProperties } from '../toolbar/Toolbar';
import { Dom } from '@utils/Dom';
import { Dragger } from '@utils/Dragger';
import { Html } from '@utils/Html';
import { Resizer } from '@utils/Resizer';
import { Utils } from '@utils/Utils';
import { Renderer } from '@utils/Renderer';
import { Exceptions } from '@utils/Exceptions';
import './Window.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 윈도우가 최상단으로 위치했을 때
     */
    front?: (me: T) => void;

    /**
     * 윈도우 크기가 최대화되었을 때
     */
    maximize?: (me: T) => void;

    /**
     * 윈도우 크기가 최소화되었을 때
     */
    minimize?: (me: T) => void;

    /**
     * 윈도우 크기가 복원되었을 때
     */
    restore?: (me: T, beforeStatus: 'maximized' | 'minimized') => void;

    /**
     * 윈도우가 보이기 전
     */
    beforeShow?: (me: T) => Promise<void>;

    /**
     * 윈도우가 보여졌을 때
     */
    show?: (me: T) => void;

    /**
     * 윈도우가 숨겨졌을 때
     */
    hide?: (me: T) => void;

    /**
     * 윈도우가 닫혔을 때
     */
    close?: (me: T) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 모달창 여부
     */
    modal?: boolean;

    /**
     * 윈도우내 첫번째 컴포넌트에 자동으로 포커스할지 여부
     */
    autoFocus?: boolean;

    /**
     * 윈도우 역할
     */
    role?: string;

    /**
     * 윈도우를 먼저 보이고, 내부 콘텐츠를 렌더링할지 여부
     */
    isLazyRender?: boolean;

    /**
     * X 축 좌표 (NULL 인 경우 수평 가운데 정렬)
     */
    x?: number;

    /**
     * Y 축 좌표 (NULL 인 경우 수직 가운데 정렬)
     */
    y?: number;

    /**
     * 윈도우의 최대크기 및 최대너비는 윈도우가 자체적으로 설정한다.
     */
    maxWidth?: never;
    maxHeight?: never;

    /**
     * 최대화허용 여부
     */
    maximizable?: boolean;

    /**
     * 최소화허용 여부
     */
    minimizable?: Dom | boolean;

    /**
     * 윈도우 하위 버튼 툴바 목록
     */
    buttons?: ToolbarProperties<Toolbar>['tools'];

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Window extends parent.Panel {
    /**
     * 전체 윈도우를 관리하기 위한 맵
     */
    static windows: Map<string, Window> = new Map();

    /**
     * 뷰포트
     */
    static viewport: Viewport;

    /**
     * 최상위 윈도우의 zIndex 포지션
     */
    static position: number = 1000;

    /**
     * 컴포넌트 타입
     */
    type = 'panel';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'window';

    /**
     * 윈도우 역할
     */
    role: string = 'window';

    /**
     * 윈도우내 첫번째 컴포넌트에 자동으로 포커스할지 여부
     */
    autoFocus: boolean;

    /**
     * 윈도우를 먼저 보이고, 내부 콘텐츠를 렌더링할지 여부
     */
    isLazyRender: boolean;

    /**
     * 모달창 여부
     */
    modal: boolean | null = null;

    /**
     * 좌측
     */
    x: number | null = null;

    /**
     * 우측
     */
    y: number | null = null;

    /**
     * 최대화허용 여부 (다른 컴포넌트에서 사용된다.)
     */
    maximizable: boolean | null = null;

    /**
     * 최소화허용 여부 (Dom일 경우 해당 Dom으로 최소화)
     */
    minimizable: Dom | boolean = null;

    /**
     * 윈도우 하위 버튼 툴바 엘리먼트(bottombar와는 별개)
     */
    buttons: Toolbar = null;

    /**
     * Task 컨트롤러 객체
     */
    task: Task = null;

    /**
     * 윈도우가 띄워진 시각 기록 (마지막 modal 을 계산하기 위함)
     */
    openedAt: number = null;

    /**
     * 윈도우 드래그 전 위치 저장
     */
    mover = {
        top: 0,
        left: 0,
    };

    /**
     * 윈도우의 zIndex
     */
    zIndex: number = -1;

    /**
     * 최대화, 최소화 전 크기
     */
    restoreRect: DOMRect = null;

    /**
     * 윈도우 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Omit<Properties<Window>, 'layout'> = null) {
        super(properties);

        this.modal = this.properties?.modal === true;
        this.role = this.properties?.role ?? 'window';
        this.autoFocus = this.properties?.autoFocus === true;
        this.isLazyRender = this.properties.isLazyRender === true;
        this.x = this.properties?.x ?? null;
        this.y = this.properties?.y ?? null;
        this.maximizable = this.properties?.maximizable === true;
        this.minimizable = this.properties?.minimizable ?? null;
        this.movable = this.properties?.movable !== false;
        this.keyboard = true;
        this.scrollable = this.properties?.scrollable ?? false;
        this.$scrollable = this.$getContent();

        this.setLayout(this.properties?.height === undefined ? 'auto' : 'fit');

        this.buttons = this.createButtons(this.properties?.buttons ?? null);

        this.title.setClosable(this.closable);
        this.title.setMaximizable(this.maximizable);
        this.title.setMinimizable(this.minimizable !== null);
        Window.windows.set(this.id, this);
    }

    /**
     * 뷰포트를 지정한다.
     *
     * @param viewport - 뷰포트
     */
    static setViewport(viewport: Viewport): void {
        Window.viewport = viewport;
    }

    /**
     * 뷰포트를 가져온다.
     *
     * @param viewport - 뷰포트
     */
    getViewport(): Viewport {
        return Window.viewport;
    }

    /**
     * 컴포넌트 최상위 DOM 을 가져온다.
     *
     * @returns 최상위 DOM
     */
    $getComponent(): Dom {
        if (this.$component === null) {
            this.$component = Html.el(document.createElement('dialog')) as Dom<HTMLDialogElement>;
            this.$component.setAttr('data-component', this.id);
            this.$component.setAttr('data-type', this.type);
            this.$component.setAttr('data-variant', this.variant);
            this.$component.setAttr('data-layout', this.layout);
            this.$component.setAttr('data-role', this.role);

            if (this.className !== null) {
                this.$component.addClass(...(Array.isArray(this.className) ? this.className : [this.className]));
            }

            this.$component.on('cancel', (_, e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
            });

            this.$component.on('close', () => {
                /**
                 * @todo 다른 윈도우가 닫힐 때 의도치 않게 이 윈도우가 닫히는 것을 방지
                 * 이 코드가 어디에 쓰이는지 현재 알 수 없음
                if (this.task == null && Window.windows.has(this.id)) {
                    const dialog = this.$getComponent().getEl() as HTMLDialogElement;
                    if (!dialog.open) {
                        if (this.modal === true) {
                            dialog.showModal();
                        } else {
                            dialog.show();
                        }
                    }
                }
                */
            });
        }

        return this.$component;
    }

    /**
     * 윈도우 위치를 설정한다.
     */
    setPosition(): void {
        if (this.isMinimized() == true || this.isMaximized() == true) {
            return;
        }

        if (this.width !== null) {
            this.$getComponent().setStyle('width', this.width + 'px');
        } else {
            this.$getComponent().setStyle('width', null);
        }

        if (this.height !== null) {
            this.$getComponent().setStyle('height', this.height + 'px');
        } else {
            this.$getComponent().setStyle('height', null);
        }

        if (this.x === null && this.y === null) {
            this.$getComponent().setStyle('position', 'fixed').setStyle('inset', '0').setStyle('margin', 'auto');
        } else if (this.x !== null && this.y === null) {
            this.$getComponent()
                .setStyle('position', 'fixed')
                .setStyle('margin', 'auto 0')
                .setStyle('left', `${this.x}px`);
        } else if (this.x === null && this.y !== null) {
            this.$getComponent()
                .setStyle('position', 'fixed')
                .setStyle('margin', '0 auto')
                .setStyle('top', `${this.y}px`);
        } else {
            this.$getComponent()
                .setStyle('position', 'fixed')
                .setStyle('margin', '0')
                .setStyle('top', `${this.y}px`)
                .setStyle('left', `${this.x}px`)
                .setStyle('right', 'unset')
                .setStyle('bottom', 'unset');
        }
    }

    /**
     * 컴포넌트 숨김여부를 설정한다.
     *
     * @param hidden - 숨김여부
     */
    async setHidden(hidden: boolean): Promise<void> {
        if (this.hidden == hidden) {
            return;
        }

        if (this.hidden == true) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * 윈도우를 출력한다.
     */
    async show(is_fire_event: boolean = true): Promise<void> {
        /**
         * 작업표시줄이 존재할 경우 작업표시줄에 창 작업을 추가하고 로딩상태로 변경한다.
         */
        if (Window.viewport.taskbar !== null && this.task === null) {
            if (this.role === 'window') {
                // @todo 모달창의 경우 작업표시줄에 등록해야하나?
                this.task = await Window.viewport.taskbar.addWindow(this);
                this.task.setLoading(true);
            }
        }

        this.openedAt ??= performance.now();

        /**
         * 윈도우 렌더링이 되지 않은 경우, 렌더링을 하고, 포지션을 설정한다.
         */
        if (this.isRendered() == false) {
            Html.get('*[data-role=aui]').append(this.$getComponent());
            if (this.isLazyRender == true) {
                await this.renderDom();
            } else {
                try {
                    await Renderer.request(() => this.render());
                } catch (e) {
                    Exceptions.print(Exceptions.base(e), async () => {
                        await this.close();
                    });
                    return;
                }
            }

            if (is_fire_event == true) {
                await this.fireEvent('beforeShow', [this]);
            }

            const dialog = this.$getComponent().getEl() as HTMLDialogElement;
            dialog.show();

            this.focus();

            if (this.height === null) {
                this.setLayout('auto');
            } else {
                this.setLayout('fit');
            }

            this.setPosition();

            if (this.isLazyRender == true) {
                try {
                    await Renderer.request(() => this.render());
                } catch (e) {
                    Exceptions.print(Exceptions.base(e), async () => {
                        await this.close();
                    });
                    return;
                }

                this.setPosition();
            }

            this.getScroller()?.updatePosition();

            if (this.movable === true && this.title !== null) {
                this.$getComponent().setData('move', 'true');
                const drag = new Dragger(this.title.$getElement(), {
                    listeners: {
                        start: () => this.handleDragStart(),
                        drag: (_, tracker: Dragger) => this.handleDrag(tracker),
                        end: () => this.handleDragEnd(),
                    },
                });

                this.title.$getElement().setData('drag', drag, false);
            }
        } else {
            if (is_fire_event == true) {
                await this.fireEvent('beforeShow', [this]);
            }

            const dialog = this.$getComponent().getEl() as HTMLDialogElement;
            dialog.show();
        }

        this.task?.setLoading(false);
        this.focus(this.autoFocus);

        if (is_fire_event == true) {
            await this.fireEvent('show', [this]);
        }
    }

    /**
     * 컴포넌트에 포커스를 지정한다.
     *
     * @param is_auto_focus - 포커스되었을 때 내부 요소의 적당한 요소에 포커스를 지정할지 여부
     */
    focus(is_auto_focus: boolean = false): void {
        this.toFront();

        if (is_auto_focus == true) {
            if (this.getItemAt(0)?.type == 'panel') {
                this.getItemAt(0).focus();
                return;
            } else if (this.getButtonAt(-1) !== null) {
                this.getButtonAt(-1).focus();
                return;
            }
        }

        super.focus();
    }

    /**
     * 최상단에 위치시킨다.
     */
    toFront(): void {
        if (this.zIndex === -1 || Window.position !== this.zIndex) {
            this.zIndex = Window.position + 1;
            this.$component.setStyle('z-index', this.zIndex);
            Window.position = this.zIndex;

            this.fireEvent('front', [this]);
        }
    }

    /**
     * 윈도우를 숨긴다.
     */
    async hide(is_fire_event: boolean = true): Promise<void> {
        this.hidden = true;
        const dialog = this.$getComponent().getEl() as HTMLDialogElement;
        dialog.close();

        if (is_fire_event == true) {
            this.fireEvent('hide', [this]);
        }
    }

    /**
     * 윈도우를 닫는다.
     */
    async close(): Promise<void> {
        const dialog = this.$getComponent().getEl() as HTMLDialogElement;
        if (dialog.open) {
            dialog.close();
        }

        await this.fireEvent('close', [this]);
        await this.remove();
    }

    /**
     * 윈도우를 최대화한다.
     *
     * @param isMaximize - 윈도우가 최대화되었는지 여부
     */
    async maximize(): Promise<void> {
        if (this.maximizable === false || (this.isMinimized() == false && this.isMaximized() == true)) {
            return;
        }

        if (this.$getComponent().hasClass('resizing') == true) {
            return;
        }

        /**
         * 최소화 상태에서 복원되는 경우, 이미 크기가 지정되어 있으므로 따로 고정크기를 지정하지 않는다.
         */
        let isMinimized = this.isMinimized();
        if (isMinimized == true) {
            this.$getComponent().removeClass('minimize');
            this.$getComponent().addClass('resizing');
        } else {
            await this.#setFixed(true);
        }

        return new Promise((resolve) => {
            this.$getComponent().animate(
                {
                    top: '0px',
                    left: '0px',
                    width: '100vw',
                    height: '100vh',
                    opacity: 1,
                },
                { duration: 300, easing: 'ease-in-out', fill: 'forwards' },
                () => {
                    this.$getComponent().stop();
                    this.$getComponent().addClass('maximize');
                    this.#setFixed(false).then(() => {
                        this.setPosition();

                        /**
                         * 최소화상태였다면, restore 이벤트도 함께 전달한다.
                         */
                        if (isMinimized == true) {
                            this.fireEvent('restore', [this, 'minimized']);
                        }
                        this.fireEvent('maximize', [this]);
                        resolve();
                    });
                }
            );
        });
    }

    /**
     * 윈도우를 최소화한다.
     */
    async minimize(): Promise<void> {
        if (this.minimizable === null || this.isMinimized() == true) {
            return;
        }

        if (this.$getComponent().hasClass('resizing') == true) {
            return;
        }

        let targetRect: DOMRect = null;
        if (this.minimizable instanceof Dom) {
            targetRect = await this.minimizable.getRect();
        } else if (this.task !== null) {
            targetRect = await this.task?.$getElement().getRect();
        }

        if (targetRect !== null) {
            await this.#setFixed(true);

            return new Promise((resolve) => {
                this.$getComponent().animate(
                    {
                        top: targetRect.top + 'px',
                        left: targetRect.left + 'px',
                        width: targetRect.width + 'px',
                        height: targetRect.height + 'px',
                        opacity: 0,
                    },
                    { duration: 300, easing: 'ease-in-out', fill: 'forwards' },
                    () => {
                        this.$getComponent().stop();
                        this.$getComponent().addClass('minimize');
                        this.#setFixed(false).then(() => {
                            this.fireEvent('minimize', [this]);
                            resolve();
                        });
                    }
                );
            });
        } else {
            this.hide(false);
            this.fireEvent('minimize', [this]);
        }
    }

    /**
     * 원래 창크기로 변경한다.
     */
    async restore(): Promise<void> {
        if (this.isRestorable() == false) {
            return;
        }

        if (this.$getComponent().hasClass('resizing') == true) {
            return;
        }

        let beforeStatus = 'maximized';
        if (this.$getComponent().hasClass('minimize') == true) {
            /**
             * 이전 상태가 최대화상태였다면, 최대화함수를 호출한다.
             */
            if (this.isMaximized() == true) {
                return await this.maximize();
            }

            this.$getComponent().addClass('resizing');
            this.$getComponent().removeClass('minimize');
            beforeStatus = 'minimized';
        } else {
            await this.#setFixed(true);

            this.$getComponent().addClass('resizing');
            this.$getComponent().removeClass('maximize');
        }

        if (this.restoreRect !== null) {
            return new Promise((resolve) => {
                this.$getComponent().animate(
                    {
                        top: this.restoreRect.top + 'px',
                        left: this.restoreRect.left + 'px',
                        width: this.restoreRect.width + 'px',
                        height: this.restoreRect.height + 'px',
                        opacity: 1,
                    },
                    { duration: 300, easing: 'ease-in-out', fill: 'forwards' },
                    () => {
                        this.$getComponent().stop();
                        this.#setFixed(false).then(() => {
                            this.setPosition();

                            this.fireEvent('restore', [this, beforeStatus]);
                            resolve();
                        });
                    }
                );
            });
        } else {
            this.fireEvent('restore', [this, beforeStatus]);
        }
    }

    /**
     * 이전크기로 돌아갈 수 있는지 여부를 확인한다.
     *
     * @returns 이전크기로 돌아갈 수 있는지 여부
     */
    isRestorable(): boolean {
        return this.$getComponent().hasClass('minimize') == true || this.$getComponent().hasClass('maximize') == true;
    }

    /**
     * 최대화상태인지 확인한다.
     *
     * @returns 최대화상태여부
     */
    isMaximized(): boolean {
        return this.$getComponent().hasClass('maximize') == true;
    }

    /**
     * 최소화상태인지 확인한다.
     *
     * @returns 최소화상태여부
     */
    isMinimized(): boolean {
        return this.$getComponent().hasClass('minimize') == true;
    }

    /**
     * 버튼 툴바에서 컴포넌트를 가져온다.
     *
     * @param index - 가져올 버튼 인덱스
     * @returns 버튼
     */
    getButtonAt<T extends Component = Button>(index: number): T {
        const buttons: Component[] = [];
        for (const button of this.buttons?.getItems() ?? []) {
            if (button instanceof Component) {
                buttons.push(button);
            }
        }

        return (buttons.at(index) as T) ?? null;
    }

    /**
     * 하위 버튼 툴바 엘리먼트을 생성한다.
     *
     * @param buttons - 버튼객체
     * @returns 하위 버튼 툴바 엘리먼트
     */
    protected createButtons(buttons: ToolbarProperties<Toolbar>['tools']): Toolbar | null {
        if (buttons === null) {
            return null;
        }

        let isRight: boolean = false;
        const components: (string | Component)[] = [];
        buttons.forEach((button) => {
            if (button == '->') {
                isRight = true;
            }
            components.push(button);
        });

        if (isRight == false) {
            components.unshift('->');
        }

        const toolbar = new Toolbar({ tools: components });

        toolbar.setParent(this);
        toolbar.setPosition('buttons');

        return toolbar;
    }

    /**
     * 푸터를 렌더링한다.
     */
    protected async renderFooter(): Promise<void> {
        super.renderFooter();

        if (this.buttons !== null) {
            this.$getFooter().append(this.buttons.$getComponent());
            this.buttons.setParent(this);
            await this.buttons.render();
        }
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        this.$getContainer().append(this.$getHeader());
        this.$getContainer().append(this.$getContent());
        this.$getContainer().append(this.$getFooter());

        await super.renderDom();

        if (this.modal == true) {
            this.$getComponent().addClass('modal');
        }

        /**
         * 윈도우 모양을 먼저 구성하기 위해 DOM 조립 후 헤더와 푸터먼저 렌더링한다.
         */
        await this.renderHeader();
        await this.renderFooter();

        if (this.scrollable !== false) {
            await this.getScroller()?.render();
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    async renderContainer(is_force: boolean = false): Promise<void> {
        await this.renderItems(is_force);
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();

        if (this.resizable == false || this.resizer !== null) return;

        if (this.resizeNm !== null) {
            const storeResize = Utils.session(`${this.type}_${this.variant}_${this.resizeNm}`) ?? null;

            if (storeResize !== null) {
                this.setLayout('fit');
                this.$getComponent().setStyle('height', this.$getComponent().getOuterHeight() + 'px');
                this.setWidth(storeResize.width);
                this.setHeight(storeResize.height);
            }
        }

        const directions: [boolean, boolean, boolean, boolean] =
            this.resizable === true ? [true, true, true, true] : this.resizable;

        this.resizer = new Resizer(this.$component, this.$component.getParent(), {
            directions: directions,
            guidelines: directions,
            minWidth: (typeof this.minWidth === 'string' ? parseFloat(this.minWidth) : this.minWidth) ?? 0,
            maxWidth: (typeof this.maxWidth === 'string' ? parseFloat(this.maxWidth) : this.maxWidth) ?? 0,
            listeners: {
                end: (_, rect: DOMRect) => {
                    const { left, top, width, height } = rect;
                    this.x = left;
                    this.y = top;

                    this.setLayout('fit');
                    if (this.variant === 'window') {
                        this.$getComponent()
                            .setStyle('transform', '')
                            .setStyle('left', `${left}px`)
                            .setStyle('top', `${top}px`)
                            .setStyle('right', 'unset')
                            .setStyle('bottom', 'unset');
                    }

                    if (directions[1] || directions[3]) this.setWidth(width);
                    if (directions[0] || directions[2]) this.setHeight(height);

                    if (this.resizeNm !== null) {
                        Utils.session(`${this.type}_${this.variant}_${this.resizeNm}`, {
                            width: width,
                            height: height,
                        });
                    }

                    this.fireEvent('resize', [this, width, height]);
                },
            },
        });
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        await this.waitForEvents();

        this.task = null;
        Window.windows.delete(this.getId());

        /**
         * 다른 윈도우가 열려져 있는 경우, 가장 최상단에 올라온 윈도우에 포커스를 지정한다.
         */
        let frontWindow: Window = null;
        Window.windows.values().forEach((window) => {
            if (window.isMinimized() == false) {
                if (frontWindow?.zIndex ?? 0 < window.zIndex) {
                    frontWindow = window;
                }
            }
        });

        frontWindow?.focus();

        if (Window.windows.size === 0) {
            Window.position = 1000;
        }

        await super.remove(removeDom);
    }

    /**
     * 드래그 시작 시 호출되는 핸들러
     */
    private handleDragStart(): void {
        if (this.isMaximized() == true) return;
        this.$getComponent().addClass('moving');

        const rect = this.$getComponent().getEl().getBoundingClientRect();
        this.mover.left = rect.left;
        this.mover.top = rect.top;

        this.$getComponent()
            .setStyle('inset', 'unset')
            .setStyle('left', `${rect.left}px`)
            .setStyle('top', `${rect.top}px`)
            .setStyle('right', 'unset')
            .setStyle('bottom', 'unset')
            .setStyle('margin', '0');
    }

    /**
     * 드래그 중일 때 호출되는 핸들러
     */
    private handleDrag(tracker: Dragger): void {
        if (this.isMaximized() == true) return;
        const { x, y } = tracker.getLength();

        const position = {
            x: x + this.mover.left,
            y: y + this.mover.top,
        };

        if (Window.viewport.taskbar !== null) {
            const taskbarHeight = Window.viewport.taskbar.$getComponent().getHeight();
            if (position.y < taskbarHeight) position.y = taskbarHeight;
        }

        this.x = position.x;
        this.y = position.y;
        this.$getComponent().setStyle('left', `${position.x}px`).setStyle('top', `${position.y}px`);
    }

    /**
     * 드래그 종료 시 호출되는 핸들러
     */
    private handleDragEnd(): void {
        if (this.isMaximized() == true) return;
        this.mover.top = 0;
        this.mover.left = 0;
        this.$getComponent().removeClass('moving');
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        if (this.closable == true && e.key == 'Escape') {
            e.stopPropagation();
            this.close();
        }
        await this.fireEvent('keyboard', [e, this]);
        return false;
    }

    /**
     * 포커스 이벤트를 처리한다.
     *
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onFocus(): Promise<boolean> {
        /**
         * 현재 창이 모달이 아닌 경우, 현재 윈도우를 최상단으로 이동한다.
         */
        if (this.modal === false) {
            this.toFront();
        }

        return await super.onFocus();
    }

    /**
     * 윈도우가 최소화, 최대화 되는 애니메이션의 시작지점을 위해 현재 스타일을 고정한다.
     */
    async #setFixed(is_fixed: boolean): Promise<void> {
        if (is_fixed == true) {
            /**
             * 윈도우 크기가 변경되는 동안 내부 콘텐츠의 크기가 가변적으로 변할 경우,
             * 내부 컴포넌트의 resize 이벤트 및 repaint 가 과하게 일어날 수 있으므로, 내부 크기를 고정한다.
             */
            const container = await this.$getContainer().getRect();
            this.$getContainer().setStyle('width', container.width + 'px');
            this.$getContainer().setStyle('height', container.height + 'px');

            /**
             * 현재 윈도우 크기 및 위치를 구한다.
             * 최대화 최소화 상태가 아니라면 차후 복원되는 윈도우 크기 및 위치로 기억한다.
             */
            const dialog = await this.$getComponent().getRect();
            if (
                this.$getComponent().hasClass('maximize') == false &&
                this.$getComponent().hasClass('minimize') == false
            ) {
                this.restoreRect = dialog;
            }

            this.$getComponent().setStyle('margin', null);
            this.$getComponent().setStyle('inset', null);
            this.$getComponent().setStyle('top', dialog.top + 'px');
            this.$getComponent().setStyle('left', dialog.left + 'px');
            this.$getComponent().setStyle('width', dialog.width + 'px');
            this.$getComponent().setStyle('height', dialog.height + 'px');

            this.$getComponent().addClass('resizing');
        } else {
            this.$getComponent().removeClass('resizing');

            /**
             * 최소화 상태라면 고정크기를 해제하지 않는다.
             */
            if (this.isMinimized() == false) {
                this.$getComponent().setStyle('margin', null);
                this.$getComponent().setStyle('inset', null);
                this.$getComponent().setStyle('top', null);
                this.$getComponent().setStyle('left', null);
                this.$getComponent().setStyle('width', null);
                this.$getComponent().setStyle('height', null);
                this.$getComponent().setStyle('opacity', null);

                this.$getContainer().setStyle('width', null);
                this.$getContainer().setStyle('height', null);
            }
        }
    }
}
